import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { registerAccount, trialCheckout, getBillingPlans, getCheckoutStatus } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/common/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, ArrowRight, ArrowLeft, ShieldCheck, Storefront, UserCircle, CreditCard, RocketLaunch, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

const WID_KEY = "vozelto.signup.wid";
const INDUSTRIES = ["E-commerce", "Coffee Shop", "Restaurant", "Retail", "Contractor / Trades",
  "Real Estate", "Medical / Health", "Fitness", "Agency", "Professional Services", "Other"];

const StepDot = ({ n, label, active, done }) => (
  <div className="flex items-center gap-2">
    <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold border transition-colors ${done ? "bg-[#6B3DFF] border-[#6B3DFF] text-white" : active ? "border-[#6B3DFF] text-[#6B3DFF]" : "border-white/15 text-[#5a5a5a]"}`}>
      {done ? <Check size={13} weight="bold" /> : n}
    </div>
    <span className={`text-xs hidden sm:inline ${active || done ? "text-white" : "text-[#5a5a5a]"}`}>{label}</span>
  </div>
);

export default function SignupPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const sessionId = params.get("session_id");
  const cancelled = params.get("checkout") === "cancelled";

  const [step, setStep] = useState(sessionId ? 4 : 1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [acct, setAcct] = useState({ first_name: "", last_name: "", email: "", password: "", confirm: "" });
  const [ws, setWs] = useState({ company_name: "", industry: "E-commerce", website: "" });
  const [plan, setPlan] = useState(params.get("plan") || "growth");
  const [interval, setBillInterval] = useState(params.get("interval") || "monthly");
  const [plans, setPlans] = useState([]);
  const [trialStatus, setTrialStatus] = useState("checking"); // checking | active | failed

  useEffect(() => {
    getBillingPlans().then((r) => setPlans(r.plans || [])).catch(() => {});
    document.title = "Start Your Free Trial — Vozelto";
  }, []);
  useEffect(() => {
    if (cancelled) { setStep(3); toast.info("Checkout cancelled — pick a plan whenever you're ready."); }
  }, [cancelled]);
  // returning from a cancelled checkout while already registered/logged in
  useEffect(() => {
    if (!sessionId && user && localStorage.getItem(WID_KEY) && step === 1) setStep(3);
  }, [user]); // eslint-disable-line

  // Step 4: poll trial activation after Stripe redirect
  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries += 1;
      try {
        const s = await getCheckoutStatus(sessionId);
        if (s.status === "complete") { setTrialStatus("active"); localStorage.removeItem(WID_KEY); return; }
        if (s.status === "expired") { setTrialStatus("failed"); return; }
      } catch { /* keep polling */ }
      if (tries < 8) setTimeout(poll, 2500);
      else setTrialStatus("failed");
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  const next1 = (e) => {
    e.preventDefault();
    setError("");
    if (acct.password.length < 8) return setError("Password must be at least 8 characters.");
    if (acct.password !== acct.confirm) return setError("Passwords don't match.");
    setStep(2);
  };
  const next2 = (e) => {
    e.preventDefault();
    setError("");
    if (!ws.company_name.trim()) return setError("Enter your company or brand name.");
    setStep(3);
  };

  const startTrial = async () => {
    setBusy(true);
    setError("");
    try {
      let wid = localStorage.getItem(WID_KEY);
      if (!user || !wid) {
        const r = await registerAccount({ ...acct, confirm: undefined, company_name: ws.company_name, industry: ws.industry, website: ws.website });
        wid = r.workspace_id;
        localStorage.setItem(WID_KEY, wid);
        await refreshUser();
      }
      const co = await trialCheckout({ plan_id: plan, interval, workspace_id: wid, origin_url: window.location.origin });
      window.location.href = co.url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong — please try again.";
      setError(msg);
      if (e?.response?.status === 409) setError(`${msg}`);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D14] text-white grain-bg flex flex-col" data-testid="signup-page">
      <div className="absolute -top-24 -left-24 h-[26rem] w-[26rem] rounded-full bg-[#6B3DFF]/12 blur-[120px] pointer-events-none" />
      <header className="relative z-10 max-w-5xl w-full mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/"><Logo variant="horizontal" height={30} showTagline={false} /></Link>
        <div className="text-sm text-[#9a9a9a]">
          Already have an account?{" "}
          <Link to="/login" data-testid="signup-signin-link" className="brand-text font-semibold hover:underline">Sign In</Link>
        </div>
      </header>

      <main className="relative z-10 flex-1 w-full max-w-2xl mx-auto px-5 py-8">
        {step < 4 && (
          <div className="flex items-center justify-between mb-8" data-testid="signup-steps">
            <StepDot n={1} label="Account" active={step === 1} done={step > 1} />
            <div className="h-px flex-1 bg-white/10 mx-3" />
            <StepDot n={2} label="Workspace" active={step === 2} done={step > 2} />
            <div className="h-px flex-1 bg-white/10 mx-3" />
            <StepDot n={3} label="Plan & Trial" active={step === 3} done={false} />
          </div>
        )}

        {error && (
          <div data-testid="signup-error" role="alert" className="mb-4 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
            {error} {error.includes("already exists") && <Link to="/login" className="underline font-semibold">Sign in</Link>}
          </div>
        )}

        {step === 1 && (
          <form onSubmit={next1} className="card-matte p-7" data-testid="signup-step-account">
            <div className="flex items-center gap-2 mb-1"><UserCircle size={20} className="brand-text" weight="fill" />
              <h1 className="font-head text-xl font-semibold">Create your account</h1></div>
            <p className="text-sm text-[#9a9a9a] mb-6">14-day free trial · No charge today · Cancel anytime</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="label-caps">First name</Label>
                <Input data-testid="signup-first-name" value={acct.first_name} onChange={(e) => setAcct({ ...acct, first_name: e.target.value })} required placeholder="Alex" className="bg-[#10101A] border-white/10 mt-1" /></div>
              <div><Label className="label-caps">Last name</Label>
                <Input data-testid="signup-last-name" value={acct.last_name} onChange={(e) => setAcct({ ...acct, last_name: e.target.value })} placeholder="Smith" className="bg-[#10101A] border-white/10 mt-1" /></div>
            </div>
            <div className="mt-4"><Label className="label-caps">Work email</Label>
              <Input data-testid="signup-email" type="email" value={acct.email} onChange={(e) => setAcct({ ...acct, email: e.target.value })} required placeholder="you@business.com" className="bg-[#10101A] border-white/10 mt-1" /></div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div><Label className="label-caps">Password</Label>
                <Input data-testid="signup-password" type="password" value={acct.password} onChange={(e) => setAcct({ ...acct, password: e.target.value })} required placeholder="At least 8 characters" className="bg-[#10101A] border-white/10 mt-1" /></div>
              <div><Label className="label-caps">Confirm password</Label>
                <Input data-testid="signup-confirm" type="password" value={acct.confirm} onChange={(e) => setAcct({ ...acct, confirm: e.target.value })} required placeholder="Re-enter password" className="bg-[#10101A] border-white/10 mt-1" /></div>
            </div>
            <Button data-testid="signup-next-1" type="submit" className="w-full mt-6 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white h-11 rounded-full">
              Continue <ArrowRight size={16} className="ml-1.5" weight="bold" />
            </Button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={next2} className="card-matte p-7" data-testid="signup-step-workspace">
            <div className="flex items-center gap-2 mb-1"><Storefront size={20} className="brand-text" weight="fill" />
              <h1 className="font-head text-xl font-semibold">Set up your workspace</h1></div>
            <p className="text-sm text-[#9a9a9a] mb-6">This is the brand your AI marketing team will work on.</p>
            <div><Label className="label-caps">Company / brand name</Label>
              <Input data-testid="signup-company" value={ws.company_name} onChange={(e) => setWs({ ...ws, company_name: e.target.value })} required placeholder="Black Ember Coffee" className="bg-[#10101A] border-white/10 mt-1" /></div>
            <div className="mt-4"><Label className="label-caps">Industry</Label>
              <select data-testid="signup-industry" value={ws.industry} onChange={(e) => setWs({ ...ws, industry: e.target.value })}
                className="w-full bg-[#10101A] border border-white/12 rounded-lg px-3 py-2.5 text-sm mt-1">
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select></div>
            <div className="mt-4"><Label className="label-caps">Website (optional)</Label>
              <Input data-testid="signup-website" value={ws.website} onChange={(e) => setWs({ ...ws, website: e.target.value })} placeholder="https://yourbrand.com" className="bg-[#10101A] border-white/10 mt-1" /></div>
            <div className="flex gap-3 mt-6">
              <Button data-testid="signup-back-2" type="button" variant="ghost" onClick={() => setStep(1)}
                className="rounded-full border border-white/10 text-white hover:bg-white/5 h-11 px-5"><ArrowLeft size={15} className="mr-1" /> Back</Button>
              <Button data-testid="signup-next-2" type="submit" className="flex-1 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white h-11 rounded-full">
                Continue <ArrowRight size={16} className="ml-1.5" weight="bold" />
              </Button>
            </div>
          </form>
        )}

        {step === 3 && (
          <div className="card-matte p-7" data-testid="signup-step-plan">
            <div className="flex items-center gap-2 mb-1"><CreditCard size={20} className="brand-text" weight="fill" />
              <h1 className="font-head text-xl font-semibold">Choose your plan</h1></div>
            <p className="text-sm text-[#9a9a9a] mb-5">Every plan starts with a <span className="text-white font-semibold">14-day free trial</span>. Your card is saved securely with Stripe but <span className="text-white font-semibold">not charged today</span>.</p>
            <div className="inline-flex items-center rounded-full border border-white/10 bg-[#14141F] p-1 mb-5" data-testid="signup-interval-toggle">
              {["monthly", "yearly"].map((iv) => (
                <button key={iv} data-testid={`signup-interval-${iv}`} onClick={() => setBillInterval(iv)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${interval === iv ? "bg-[#6B3DFF] text-white" : "text-[#9a9a9a] hover:text-white"}`}>
                  {iv === "monthly" ? "Monthly" : "Yearly (save ~2mo)"}
                </button>
              ))}
            </div>
            <div className="space-y-2.5">
              {plans.map((p) => (
                <button key={p.id} data-testid={`signup-plan-${p.id}`} onClick={() => setPlan(p.id)}
                  className={`w-full text-left rounded-xl border p-4 transition-all ${plan === p.id ? "border-[#6B3DFF] bg-[#6B3DFF]/[0.07]" : "border-white/10 hover:border-white/25"}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${plan === p.id ? "border-[#6B3DFF]" : "border-white/25"}`}>
                        {plan === p.id && <div className="h-2 w-2 rounded-full bg-[#6B3DFF]" />}
                      </div>
                      <span className="font-head font-semibold">{p.name}</span>
                      {p.id === "growth" && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-[#6B3DFF] text-white">RECOMMENDED</span>}
                    </div>
                    <div className="font-head font-bold">${interval === "monthly" ? p.monthly : p.yearly}<span className="text-xs text-[#7a7a7a] font-normal">/{interval === "monthly" ? "mo" : "yr"}</span></div>
                  </div>
                  <div className="text-xs text-[#9a9a9a] mt-1.5 ml-6 line-clamp-1">{(p.features || []).slice(0, 3).join(" · ")}</div>
                </button>
              ))}
              {!plans.length && <div className="text-sm text-[#7a7a7a] text-center py-4">Loading plans…</div>}
            </div>
            <div className="flex gap-3 mt-6">
              {!user && (
                <Button data-testid="signup-back-3" type="button" variant="ghost" onClick={() => setStep(2)}
                  className="rounded-full border border-white/10 text-white hover:bg-white/5 h-11 px-5"><ArrowLeft size={15} className="mr-1" /> Back</Button>
              )}
              <Button data-testid="signup-start-trial" onClick={startTrial} disabled={busy || !plans.length}
                className="flex-1 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white h-11 rounded-full">
                <Lightning size={16} className="mr-1.5 shrink-0" weight="fill" />
                <span className="truncate">{busy ? "Setting up your trial…" : "Start 14-Day Free Trial"}</span>
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-[#7a7a7a]">
              <span className="flex items-center gap-1"><ShieldCheck size={12} className="brand-text" /> Secure Stripe checkout</span>
              <span className="flex items-center gap-1"><Check size={12} className="brand-text" weight="bold" /> No charge for 14 days</span>
              <span className="flex items-center gap-1"><Check size={12} className="brand-text" weight="bold" /> Cancel anytime</span>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="card-matte p-10 text-center" data-testid="signup-step-success">
            {trialStatus === "checking" && (
              <>
                <div className="h-14 w-14 rounded-full bg-[#6B3DFF]/15 flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <RocketLaunch size={26} className="brand-text" weight="fill" />
                </div>
                <h1 className="font-head text-2xl font-bold">Activating your trial…</h1>
                <p className="text-sm text-[#9a9a9a] mt-2" data-testid="signup-activating">Confirming your subscription with Stripe — a few seconds.</p>
              </>
            )}
            {trialStatus === "active" && (
              <>
                <div className="h-14 w-14 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-4">
                  <Check size={28} className="text-emerald-400" weight="bold" />
                </div>
                <h1 className="font-head text-2xl font-bold" data-testid="signup-trial-active">Your 14-day free trial is live! 🎉</h1>
                <p className="text-sm text-[#9a9a9a] mt-2 max-w-md mx-auto">Your AI marketing team is ready. You won't be charged until your trial ends — cancel anytime from Billing.</p>
                <Button data-testid="signup-goto-dashboard" onClick={() => navigate("/")}
                  className="mt-6 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-12 px-8">
                  Go to My Dashboard <ArrowRight size={17} className="ml-1.5" weight="bold" />
                </Button>
              </>
            )}
            {trialStatus === "failed" && (
              <>
                <h1 className="font-head text-2xl font-bold">Hmm, we couldn't confirm that</h1>
                <p className="text-sm text-[#9a9a9a] mt-2">Your checkout may not have completed. You can try again — nothing was charged.</p>
                <Button data-testid="signup-retry" onClick={() => { setStep(3); navigate("/signup", { replace: true }); }}
                  className="mt-6 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-11 px-6">Back to Plans</Button>
              </>
            )}
          </div>
        )}
      </main>
      <footer className="relative z-10 py-6 text-center text-xs text-[#5a5a5a]">
        © {new Date().getFullYear()} Vozelto · <Link to="/terms" className="hover:text-white">Terms</Link> · <Link to="/privacy" className="hover:text-white">Privacy</Link>
      </footer>
    </div>
  );
}
